import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  getUserFullName
} from '@bliks-io/ui';
import {
  MULTI_FREQUENCY_DEFAULT_COUNT,
  calculateStepCapacity,
  getFrequencyMultipliers,
  isMultiFrequency,
  type FrequencyValue,
  type ManualCapacity,
  type ProcessDraftStep
} from '@bliks-io/contracts';
import {
  useRemoveDraftStepManualCapacity,
  useSetDraftStepManualCapacity
} from '@/api/mutations/useProcessDraft';
import { useOrgFunctions } from '@/api/queries/useOrgFunctions';
import { toastApiError } from '@/api/mutations/toast-api-error';
import { formatCapacityHours, resolveCapacityLocale } from '@/components/capacity-cell';
import { getEntityRefId } from '@/lib/entityRefUtils';
import { frequencyOptions } from '@/lib/formatFrequency';
import { formatDate } from '@/lib/formatters';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  step: ProcessDraftStep;
  processId: string;
}

/**
 * Enter, overwrite or remove one step's manual capacity value: the same three
 * primitives a step survey collects, previewed live before saving — including
 * the team-extrapolated total, so a team entry is legibly per person. The
 * authoritative figure still comes from the server recompute.
 */
export function DraftStepManualCapacityDialog({ open, onOpenChange, step, processId }: Props) {
  const { t, i18n } = useTranslation();
  const stored = step.manualCapacity ?? undefined;

  const [frequency, setFrequency] = useState<FrequencyValue | ''>(stored?.frequency ?? '');
  // The inputs hold raw text: "7,5" is neither empty nor a value, and the user
  // has to keep seeing what they typed to correct it.
  const [timesPerPeriodInput, setTimesPerPeriodInput] = useState(
    stored?.frequencyTimesPerPeriod?.toString() ?? ''
  );
  const [durationInput, setDurationInput] = useState(stored?.durationMinutes.toString() ?? '');

  const setManualCapacity = useSetDraftStepManualCapacity(processId);
  const removeManualCapacity = useRemoveDraftStepManualCapacity(processId);
  const pending = setManualCapacity.isPending || removeManualCapacity.isPending;

  const expectsCount = isMultiFrequency(frequency);
  const timesPerPeriod = parsePositiveInt(timesPerPeriodInput);
  const durationMinutes = parsePositiveInt(durationInput);
  const valid =
    frequency !== '' &&
    durationMinutes !== undefined &&
    (!expectsCount || timesPerPeriod !== undefined);

  const preview = valid
    ? previewHours(frequency, expectsCount ? timesPerPeriod : undefined, durationMinutes)
    : null;
  const locale = resolveCapacityLocale(i18n.language);

  // The assignee decides what the entry means (same branch as the server's
  // aggregation): a team multiplies it by its members, a role or person IS the
  // total. Unknown assignee (suggestion, deleted function) falls back to the
  // per-person wording. ≈ throughout — the server figure also subtracts
  // survey decliners and adds support staff.
  const { data: orgFunctions } = useOrgFunctions();
  const assignedOrgFunction =
    step.assignedToModel === 'OrgFunction'
      ? orgFunctions?.find((f) => f._id === getEntityRefId(step.assignedTo))
      : undefined;
  const teamSize = assignedOrgFunction?.kind === 'team' ? assignedOrgFunction.memberCount : null;
  const isSingleStream = step.assignedToModel === 'User' || assignedOrgFunction?.kind === 'role';

  const handleSave = async () => {
    if (!valid) return;
    try {
      await setManualCapacity.mutateAsync({
        stepId: String(step._id),
        value: {
          frequency,
          ...(expectsCount ? { frequencyTimesPerPeriod: timesPerPeriod } : {}),
          durationMinutes
        }
      });
      onOpenChange(false);
    } catch (error) {
      toastApiError(error, 'processDraft.capacity.saveError');
    }
  };

  const handleRemove = async () => {
    try {
      await removeManualCapacity.mutateAsync({ stepId: String(step._id) });
      onOpenChange(false);
    } catch (error) {
      toastApiError(error, 'processDraft.capacity.saveError');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('processDraft.capacity.dialogTitle')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="manualCapacityFrequency">{t('processDraft.capacity.frequency')}</Label>
            <Select
              value={frequency}
              onValueChange={(next) => {
                const value = next as FrequencyValue;
                setFrequency(value);
                setTimesPerPeriodInput(
                  isMultiFrequency(value) ? String(MULTI_FREQUENCY_DEFAULT_COUNT[value]) : ''
                );
              }}
            >
              <SelectTrigger id="manualCapacityFrequency" className="mt-2">
                <SelectValue placeholder={t('surveyDialog.frequencyPlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                {frequencyOptions(t).map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {expectsCount && (
            <div>
              <Label htmlFor="manualCapacityTimesPerPeriod">
                {t('processDraft.capacity.timesPerPeriod')}
              </Label>
              <Input
                id="manualCapacityTimesPerPeriod"
                type="text"
                inputMode="numeric"
                className="mt-2"
                value={timesPerPeriodInput}
                aria-invalid={
                  (timesPerPeriodInput !== '' && timesPerPeriod === undefined) || undefined
                }
                onChange={(e) => setTimesPerPeriodInput(e.target.value)}
              />
            </div>
          )}

          <div>
            <Label htmlFor="manualCapacityDuration">{t('processDraft.capacity.duration')}</Label>
            <Input
              id="manualCapacityDuration"
              type="text"
              inputMode="numeric"
              className="mt-2"
              value={durationInput}
              aria-invalid={(durationInput !== '' && durationMinutes === undefined) || undefined}
              onChange={(e) => setDurationInput(e.target.value)}
            />
          </div>

          {preview && teamSize != null && (
            <div className="space-y-0.5">
              <p className="text-xs text-muted-foreground">
                {t('processDraft.capacity.previewPerPerson', {
                  month: formatCapacityHours(preview.perMonth, locale),
                  year: formatCapacityHours(preview.perYear, locale)
                })}
              </p>
              <p className="text-xs font-medium text-foreground">
                {t('processDraft.capacity.previewTeamTotal', {
                  count: teamSize,
                  month: formatCapacityHours(preview.perMonth * teamSize, locale),
                  year: formatCapacityHours(preview.perYear * teamSize, locale)
                })}
              </p>
            </div>
          )}
          {preview && teamSize == null && isSingleStream && (
            <p className="text-xs text-muted-foreground">
              {t('processDraft.capacity.previewTotal', {
                month: formatCapacityHours(preview.perMonth, locale),
                year: formatCapacityHours(preview.perYear, locale)
              })}
            </p>
          )}
          {preview && teamSize == null && !isSingleStream && (
            <p className="text-xs text-muted-foreground">
              {t('processDraft.capacity.preview', {
                month: formatCapacityHours(preview.perMonth, locale),
                year: formatCapacityHours(preview.perYear, locale)
              })}
            </p>
          )}

          {stored && (
            <p className="text-xs text-muted-foreground">
              {t('processDraft.capacity.attribution', {
                name: enteredByName(stored.enteredBy),
                date: formatDate(stored.enteredAt, locale)
              })}
            </p>
          )}
        </div>

        <DialogFooter>
          {stored && (
            <Button
              variant="outline"
              onClick={() => void handleRemove()}
              loading={removeManualCapacity.isPending}
              disabled={pending}
            >
              {t('processDraft.capacity.remove')}
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={() => void handleSave()}
            loading={setManualCapacity.isPending}
            disabled={!valid || pending}
          >
            {t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Whole minutes and whole occurrences only — "7.5" and "7,5" are not a value,
 *  and stripping the separator would turn either into 75. */
function parsePositiveInt(raw: string): number | undefined {
  const parsed = Number(raw.trim());
  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
}

function previewHours(
  frequency: FrequencyValue,
  timesPerPeriod: number | undefined,
  durationMinutes: number
): { perMonth: number; perYear: number } {
  const { perYear, perMonth } = getFrequencyMultipliers(frequency, timesPerPeriod);
  return {
    perMonth: calculateStepCapacity(perMonth, durationMinutes),
    perYear: calculateStepCapacity(perYear, durationMinutes)
  };
}

/** Populated on this route only; `null` once the entering user is hard-deleted.
 *  Neither an unpopulated id nor a dangling ref has a name to attribute. */
function enteredByName(enteredBy: ManualCapacity['enteredBy']): string {
  if (!enteredBy || typeof enteredBy === 'string') return '';
  return getUserFullName(enteredBy.firstName, enteredBy.lastName);
}
